import {
  Article,
  ArticleAction,
  ArticleFooter,
  ArticleImage,
  ArticleList,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { Badge } from "@babackapp/customer-ui/components/badge";
import { Checkbox } from "@babackapp/customer-ui/components/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/customer-ui/components/tooltip";
import { InfoIcon } from "lucide-react";
import { useLocalizeLineItem } from "../hooks/use-localize-line-item";
import { getPaidPriceInCents } from "../lib/paid-price.lib";
import { DiscountedPrice } from "./discounted-price";
import type { OrderLineItem } from "../types";
import { parseLineItemPriceInCents } from "../utils/get-line-item-price";

type ArticleRowProps = {
  item: OrderLineItem;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
  badge?: string;
  currency?: string;
  /** True when the current session is an authenticated Baback dashboard user (merchant or staff) — gates SKU visibility. */
  isConnectedUser?: boolean;
  connectedUserTooltip?: string;
};

export function ArticleRow({
  item,
  checked,
  onToggle,
  disabled = false,
  badge,
  currency = "EUR",
  isConnectedUser = false,
  connectedUserTooltip,
}: ArticleRowProps) {
  // Name and options in the portal's language (BAB-3184). Display only: the
  // price and the toggle keep reading the raw line.
  const display = useLocalizeLineItem()(item);
  const options = [
    display.variant?.option1,
    display.variant?.option2,
    display.variant?.option3,
  ]
    .filter(Boolean)
    .filter((o) => o !== "Default Title")
    .join(" • ");

  const catalogPriceCents = parseLineItemPriceInCents(item.price);

  return (
    <Article disabled={disabled} onClick={disabled ? undefined : onToggle}>
      {item.image && <ArticleImage src={item.image} alt={display.title} />}
      <ArticleTitle>{display.title}</ArticleTitle>
      {(options || catalogPriceCents != null) && (
        <ArticleSubtitle>
          {options}
          {options && catalogPriceCents != null && " • "}
          {catalogPriceCents != null && (
            <DiscountedPrice
              catalogCents={catalogPriceCents}
              paidCents={getPaidPriceInCents(item)}
              currency={currency}
            />
          )}
        </ArticleSubtitle>
      )}
      {isConnectedUser && item.sku && (
        <ArticleFooter className="flex items-center gap-1.5">
          <span>{item.sku}</span>
          <Tooltip>
            <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
              <InfoIcon className="size-3.5 cursor-help" />
            </TooltipTrigger>
            <TooltipContent>{connectedUserTooltip}</TooltipContent>
          </Tooltip>
        </ArticleFooter>
      )}
      <ArticleAction autoHide={false}>
        {badge ? (
          <Badge>{badge}</Badge>
        ) : (
          <Checkbox
            checked={checked}
            onCheckedChange={onToggle}
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </ArticleAction>
    </Article>
  );
}

export { ArticleList };
